/**
 * BotPickerModal — the host's "Select bots" popup over the lobby. Shared by the lobby's
 * seat "Add bot" action and the Add Players screen. Stays open so the host
 * can add several commanders in a row; each row flashes "Added!" then settles
 * on "In lobby".
 */

import React from 'react'
import { View, Text, Animated, TouchableOpacity, Platform } from 'react-native'
import type { GamePlayerResponse } from '@warcarp/api-types'
import { LobbyPopup } from './LobbyPopup'
import { BotAvatar } from '../../bots/BotAvatar'
import { botPickerOptions } from '../../../lib/botPicker'
import type { BotPickerStatus } from '../../../hooks/useLobbyBots'

function BotPickerStatusLabel({ status, testID }: { status: BotPickerStatus; testID: string }) {
  const opacity = React.useRef(new Animated.Value(0)).current

  React.useEffect(() => {
    opacity.setValue(0)
    const animation = Animated.timing(opacity, {
      toValue: 1,
      duration: 180,
      useNativeDriver: Platform.OS !== 'web',
    })
    animation.start()
    return () => animation.stop()
  }, [opacity, status])

  return (
    // Classes go on a plain Text: NativeWind doesn't style Animated.Text on web.
    <Animated.View style={{ opacity }}>
      <Text
        testID={testID}
        // An outlined tag in body text colour, so it reads in light and dark.
        className={`font-stencil text-xs uppercase rounded-md border px-2 py-1 text-center ${status === 'added' ? 'text-blood border-blood' : 'text-ink border-olive'}`}
        style={{ minWidth: 68 }}
      >
        {status === 'added' ? 'Added!' : 'In lobby'}
      </Text>
    </Animated.View>
  )
}

export function BotPickerModal({ visible, onClose, players, statuses, onAdd, busy, seatsOpen, seatLabel }: {
  visible: boolean
  onClose: () => void
  players: Pick<GamePlayerResponse, 'botProfile'>[]
  statuses: Record<string, BotPickerStatus>
  onAdd: (botProfile: string) => void
  /** An add (or a fill) is in flight: rows are disabled. */
  busy: boolean
  /** Open seats left; at 0 the game is full and no more bots can be added. */
  seatsOpen: number
  /** Pick-seats team lobby: the seat the bot will take ("Seat 3 · Team B"). Omitted elsewhere. */
  seatLabel?: string
}) {
  const full = seatsOpen <= 0
  const pickerOptions = botPickerOptions(players)
  return (
    <LobbyPopup visible={visible} title={seatLabel ? 'Select a bot' : 'Select bots'}
      subtitle={full ? 'The game is full. Remove a player or add seats to add more bots.' : seatLabel ? `For ${seatLabel}.` : 'Add one or more commanders to the lobby.'}
      closeLabel="Close bot picker" closeTestID="bot-picker-close" onClose={onClose} testID="bot-picker" maxWidth={520} scroll>
      {pickerOptions.map((bot) => {
        const { alreadyInGame } = bot
        // "In lobby" comes from the live roster (a removed bot is addable again);
        // the hook's status only adds the brief "Added!" flash.
        const pickerStatus = statuses[bot.key] === 'added' ? 'added' : alreadyInGame ? 'lobby' : null
        return (
          <TouchableOpacity
            key={bot.key}
            onPress={() => onAdd(bot.key)}
            disabled={alreadyInGame || busy || full}
            accessibilityLabel={alreadyInGame ? `${bot.displayName}, in lobby` : full ? `${bot.displayName}, game is full` : `Add ${bot.displayName}`}
            accessibilityState={{ disabled: alreadyInGame || busy || full }}
            accessibilityRole="button"
            className={`rounded-xl border-2 px-4 py-3 ${alreadyInGame ? 'border-olive/60 bg-paper' : 'border-olive bg-paper-up'}`}
          >
            <View className="flex-row items-center justify-between gap-3">
              <BotAvatar profile={bot.key} size={36} />
              <View className="min-w-0 flex-1 pr-2">
                <Text className="text-ink font-stencil text-base uppercase">{bot.displayName}</Text>
                <Text className="text-ink-soft font-typed text-xs mt-1" numberOfLines={2}>
                  {bot.description}
                </Text>
              </View>
              {pickerStatus ? (
                <BotPickerStatusLabel status={pickerStatus} testID={`bot-status-${bot.key}`} />
              ) : (
                <Text className={`text-right font-stencil text-xs uppercase ${full ? 'text-ink-soft' : 'text-blood'}`} style={{ minWidth: 68 }}>{full ? '—' : 'Add'}</Text>
              )}
            </View>
          </TouchableOpacity>
        )
      })}
    </LobbyPopup>
  )
}
